import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { VoiceNoteInput } from '../../components/ui/voice-note-input';
import {
  Search, Loader2, Pill, Package, AlertTriangle, RefreshCw,
  Pencil, Check, X, Database, Wifi, WifiOff, BookOpen
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ENRICH_FIELDS = [
  { key: 'usage_notes', label: 'Usage Notes', testId: 'enrich-usage-notes',
    hint: 'How is it applied/taken? Quantity? Any special technique?',
    placeholder: 'e.g. Apply thin layer at night. Use sunscreen during day.' },
  { key: 'indications', label: 'Indications', testId: 'enrich-indications',
    hint: 'Which conditions and grades/severity is this for?',
    placeholder: 'e.g. Acne grade 1-3, photoaging, comedonal acne' },
  { key: 'contraindications', label: 'Contraindications', testId: 'enrich-contraindications',
    hint: 'When should it NOT be used? Pregnancy, lactation, age, broken skin, drug interactions?',
    placeholder: 'e.g. Pregnancy, lactation, broken skin' },
  { key: 'photosensitivity', label: 'Photosensitivity', testId: 'enrich-photosensitivity',
    hint: 'Does it increase sun sensitivity? Sunscreen mandatory? Prefer night application?',
    placeholder: 'e.g. Photosensitizing — night use only, strict SPF 50 in the morning' },
  { key: 'environmental_conditions', label: 'Environmental / Weather Conditions', testId: 'enrich-environmental-conditions',
    hint: 'Which weather or season suits this? Avoid in humidity/heat/monsoon? Storage precautions in summer?',
    placeholder: 'e.g. Avoid heavy creams in humid monsoon; ideal for dry winters; store below 25°C' },
  { key: 'skin_type_suitability', label: 'Skin Type Suitability', testId: 'enrich-skin-type',
    hint: 'Which skin types tolerate it well — oily, dry, sensitive, combination?',
    placeholder: 'e.g. Best for oily/acne-prone; avoid on sensitive or barrier-damaged skin' },
  { key: 'application_timing', label: 'Application Timing', testId: 'enrich-application-timing',
    hint: 'Morning or night? How often? Any titration schedule?',
    placeholder: 'e.g. Night only, alternate days for 2 weeks, then daily' },
  { key: 'preferred_combinations', label: 'Preferred Combinations', testId: 'enrich-combinations',
    hint: 'Which medicines pair well with this? Any to keep apart (timing gaps)?',
    placeholder: 'e.g. Use with Clindamycin gel (morning), Niacinamide serum' },
];

const EMPTY_FORM = Object.fromEntries(ENRICH_FIELDS.map(f => [f.key, '']));

export function EnrichmentDialog({ medicine, open, onClose, onSaved }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [lastTranscript, setLastTranscript] = useState('');

  useEffect(() => {
    const base = { ...EMPTY_FORM };
    if (medicine?.enrichment) {
      ENRICH_FIELDS.forEach(({ key }) => { base[key] = medicine.enrichment[key] || ''; });
    }
    setForm(base);
    setLastTranscript('');
  }, [medicine]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      if (lastTranscript) payload._transcript = lastTranscript;
      await axios.put(`${API_URL}/api/pharmacy/medicines/${medicine.sku}/enrich`, payload, { withCredentials: true });
      toast.success(`Clinical notes saved for ${medicine.name}`);
      onSaved();
      onClose();
    } catch (err) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (!medicine) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="enrichment-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-lg">{medicine.name}</DialogTitle>
          <DialogDescription>
            {medicine.company} · {medicine.sku} · {medicine.category}
            {medicine.active_ingredients && ` · ${medicine.active_ingredients}`}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          {/* Voice enrichment — reusable component */}
          <VoiceNoteInput
            endpoint={`/api/pharmacy/medicines/${medicine.sku}/enrich-audio`}
            context={{}}
            onResult={(extracted, transcript) => {
              setForm(prev => {
                const next = { ...prev };
                ENRICH_FIELDS.forEach(({ key }) => { if (extracted[key]) next[key] = extracted[key]; });
                return next;
              });
              if (transcript) setLastTranscript(transcript);
            }}
            label="Dictate Notes"
          />
          {ENRICH_FIELDS.map((f) => (
            <div key={f.key} className="space-y-1">
              <Label className="text-xs font-medium">{f.label}</Label>
              <p className="text-[10px] text-muted-foreground leading-snug">{f.hint}</p>
              <Textarea
                value={form[f.key]}
                onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                placeholder={f.placeholder}
                className="rounded-xl text-sm min-h-[60px]"
                data-testid={f.testId}
              />
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-full">Cancel</Button>
          <Button onClick={handleSave} disabled={saving} className="rounded-full bg-[#0E5132] hover:bg-[#063D2F]" data-testid="save-enrichment-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Check className="w-4 h-4 mr-1" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function MedicineInventoryPage() {
  const { user } = useAuth();
  const [medicines, setMedicines] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [status, setStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [enrichMed, setEnrichMed] = useState(null);
  const [isLive, setIsLive] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => clearTimeout(t);
  }, [searchQuery]);

  const fetchMedicines = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 500 };
      if (debouncedSearch) params.q = debouncedSearch;
      const { data } = await axios.get(`${API_URL}/api/pharmacy/medicines`, { params, withCredentials: true });
      setMedicines(data.items);
      setTotal(data.total);
      setIsLive(data.live !== false);
    } catch (err) {
      console.error('Failed to fetch medicines', err);
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch]);

  const fetchStatus = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/pharmacy/status`, { withCredentials: true });
      setStatus(data);
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => { fetchMedicines(); }, [fetchMedicines]);
  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/pharmacy/sync`, {}, { withCredentials: true });
      toast.success(`Synced ${data.synced} medicines from pharmacy`);
      fetchMedicines();
      fetchStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Sync failed — pharmacy may not be reachable');
    } finally {
      setSyncing(false);
    }
  };

  const canEnrich = user?.role === 'admin' || (user?.role === 'doctor' && user?.can_enrich_medicines);

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="medicine-inventory-page">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold text-slate-800">Medicine Inventory</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Synced from Eden Pharmacy · {total} medicines
          </p>
        </div>
        <div className="flex items-center gap-3">
          {status && (
            <>
              <Badge variant="outline" className={`gap-1.5 px-3 py-1.5 ${isLive ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-amber-200 bg-amber-50 text-amber-700'}`}>
                {isLive ? <Wifi className="w-3.5 h-3.5" /> : <WifiOff className="w-3.5 h-3.5" />}
                {isLive ? 'Live from Pharmacy' : 'Cached (Pharmacy Offline)'}
              </Badge>
              <Badge variant="outline" className="gap-1.5 px-3 py-1.5 border-[rgba(14,81,50,0.15)] bg-[#E6EFEA] text-[#0E5132]">
                <BookOpen className="w-3.5 h-3.5" />
                {status.enriched} Enriched
              </Badge>
            </>
          )}
          {user?.role === 'admin' && (
            <Button onClick={handleSync} disabled={syncing} variant="outline" className="rounded-lg gap-1.5" data-testid="sync-pharmacy-btn">
              {syncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              Sync Pharmacy
            </Button>
          )}
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search by name, company, SKU, or ingredients..."
          className="pl-9 pr-9 h-9 rounded-lg"
          data-testid="medicine-search-input"
        />
        {searchQuery && (
          <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {/* Medicine list */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-[#0E5132]" />
        </div>
      ) : medicines.length === 0 ? (
        <Card className="border-dashed border-2 border-slate-200">
          <CardContent className="py-10 text-center">
            <Database className="w-8 h-8 text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">
              {debouncedSearch ? `No medicines matching "${debouncedSearch}"` : 'No medicines synced yet. Click "Sync Pharmacy" to pull the catalog.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-2">
          {medicines.map((med) => (
            <Card
              key={med.sku}
              className={`border transition-colors ${med.enrichment ? 'border-blue-100 bg-[#E6EFEA]/10' : 'border-slate-200'}`}
              data-testid={`medicine-${med.sku}`}
            >
              <CardContent className="py-3 px-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${med.in_stock ? 'bg-emerald-50' : 'bg-red-50'}`}>
                      <Pill className={`w-4 h-4 ${med.in_stock ? 'text-emerald-600' : 'text-red-400'}`} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-medium text-sm truncate">{med.name}</p>
                        {med.enrichment && (
                          <Badge className="h-4 px-1.5 text-[9px] bg-[#E6EFEA] text-[#0E5132] border-[rgba(14,81,50,0.15)] hover:bg-[#E6EFEA] gap-0.5 shrink-0">
                            <BookOpen className="w-2.5 h-2.5" /> Notes
                          </Badge>
                        )}
                        {!med.in_stock && (
                          <Badge variant="outline" className="h-4 px-1.5 text-[9px] border-red-200 text-red-500 shrink-0">
                            <AlertTriangle className="w-2.5 h-2.5 mr-0.5" /> Out of stock
                          </Badge>
                        )}
                        {med.is_archived && (
                          <Badge variant="outline" className="h-4 px-1.5 text-[9px] border-amber-200 text-amber-700 bg-amber-50 shrink-0" title={med.archive_reason || 'Archived by pharmacy'} data-testid={`discontinued-badge-${med.sku}`}>
                            <AlertTriangle className="w-2.5 h-2.5 mr-0.5" /> Discontinued
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground truncate">
                        {med.company} · {med.category} · {med.sku}
                        {med.active_ingredients && ` · ${med.active_ingredients}`}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0 ml-3">
                    <div className="text-right">
                      <p className="text-xs font-medium">₹{med.selling_price}</p>
                      <p className="text-[10px] text-muted-foreground">{med.stock_qty} {med.unit}</p>
                    </div>
                    {canEnrich && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-slate-400 hover:text-[#0E5132] hover:bg-[#E6EFEA]"
                        onClick={() => setEnrichMed(med)}
                        data-testid={`enrich-${med.sku}`}
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
                {/* Show enrichment preview inline */}
                {med.enrichment && (
                  <div className="mt-2 ml-12 grid grid-cols-2 gap-x-4 gap-y-1 text-[10px]">
                    {med.enrichment.indications && (
                      <div><span className="text-[#0E5132] font-medium">Indications:</span> <span className="text-slate-500">{med.enrichment.indications}</span></div>
                    )}
                    {med.enrichment.contraindications && (
                      <div><span className="text-red-500 font-medium">Contraindications:</span> <span className="text-slate-500">{med.enrichment.contraindications}</span></div>
                    )}
                    {med.enrichment.usage_notes && (
                      <div><span className="text-slate-600 font-medium">Usage:</span> <span className="text-slate-500">{med.enrichment.usage_notes}</span></div>
                    )}
                    {med.enrichment.preferred_combinations && (
                      <div><span className="text-emerald-600 font-medium">Combos:</span> <span className="text-slate-500">{med.enrichment.preferred_combinations}</span></div>
                    )}
                    {med.enrichment.photosensitivity && (
                      <div><span className="text-amber-600 font-medium">Sun:</span> <span className="text-slate-500">{med.enrichment.photosensitivity}</span></div>
                    )}
                    {med.enrichment.environmental_conditions && (
                      <div><span className="text-sky-600 font-medium">Environment:</span> <span className="text-slate-500">{med.enrichment.environmental_conditions}</span></div>
                    )}
                    {med.enrichment.skin_type_suitability && (
                      <div><span className="text-violet-600 font-medium">Skin type:</span> <span className="text-slate-500">{med.enrichment.skin_type_suitability}</span></div>
                    )}
                    {med.enrichment.application_timing && (
                      <div><span className="text-teal-600 font-medium">Timing:</span> <span className="text-slate-500">{med.enrichment.application_timing}</span></div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <EnrichmentDialog
        medicine={enrichMed}
        open={!!enrichMed}
        onClose={() => setEnrichMed(null)}
        onSaved={fetchMedicines}
      />
    </div>
  );
}
